/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
import { Style } from 'ol/style';
import Filter from './filter/Filter';
import LayerConfig from './LayerConfig';
import LayerStyle from './LayerStyle';
import MapUtils from './MapUtils';
import StyleRule from './style/StyleRule';
import { Feature } from 'ol';

export default class LayerStyleComplex {
    
    styleRules: StyleRule[] = [];

    displayInLayerPanel = false;

    /** Cache des styles fusionnés (couleur + taille) pour éviter un clone/fetch SVG à chaque entité. */
    private mergedStyleCache: { [key: string]: LayerStyle } = {};

    constructor(input: any = null, defaultStyle:LayerStyle|null = null) {
      if (input) {
        if (input.displayInLayerPanel !== undefined) {
          this.displayInLayerPanel = input.displayInLayerPanel;
        }

        input.styleRules.forEach((styleRule: any) => {
          this.styleRules.push(new StyleRule(styleRule));
        });
      }
      if (defaultStyle !== null) {
        this.styleRules.forEach((c) => c.style.setBaseStyle(defaultStyle.getMergedBaseStyle()));
      }
    }

    clearCache() {
      this.clearMergedStyleCache();
      this.styleRules.forEach((r) => r.style.clearCache());
    }

    /** Invalide uniquement les styles fusionnés (couleur + taille), pas les règles sources. */
    clearMergedStyleCache(): void {
      this.mergedStyleCache = {};
    }

    toSld(layer: LayerConfig): string {
      let sld = '';
      this.styleRules.forEach((r) => {
        sld += LayerStyleComplex.toSldRule(r, layer);
      });

      return sld;
    }

    toJson():any {
      return {
        displayInLayerPanel: this.displayInLayerPanel,
        styleRules: this.styleRules.map((x) => x.toJson()),
      };
    }

    isDefined():any {
      return this.styleRules.length > 0;
    }

    static toSldRule(r: StyleRule, layer: LayerConfig): string {
      const label = r.label ? r.label : r.value;
      console.log(r);
      let filterText = '';
      if (!r.filter || r.value!=r.filter.cql) {
        const filter = new Filter();
        try {
          filter.fromCQL(r.value);
          r.filter = filter;
        } catch (e) {
          console.log(e);
        }
        filterText = filter.toSld();
      } else {
        filterText = r.filter.toSld();
      }
      let rule = `<Rule>
        <Name>${label}</Name>
        <Title>${label}</Title>
        <Abstract>${label}</Abstract>
        ${filterText}`;
      if (r.minScaleDenom) {
        rule += `<MinScaleDenominator>${r.minScaleDenom}</MinScaleDenominator>`;
      }
      if (r.maxScaleDenom) {
        rule += `<MaxScaleDenominator>${r.maxScaleDenom}</MaxScaleDenominator>`;
      }
      rule += `${r.style.toSld(layer)}
      </Rule>`;
      if (!r.active && layer.complexStyle.displayInLayerPanel) {
        return '';
      }
      return rule;
    }

    clone(): LayerStyleComplex {
      return new LayerStyleComplex(this.toJson());
    }


    /** Règle qui définit une couleur de remplissage (pas le stroke seul). */
    ruleDefinesColor(rule: StyleRule): boolean {
      const style = rule && rule.style;
      if (!style) {
        return false;
      }
      const fillColor = style.fillStyle && style.fillStyle.fillColor;
      const symbolFill = style.pointSymbol && style.pointSymbol.symbolFillColor;
      return !!(
        (fillColor && String(fillColor).length > 0)
        || (symbolFill && String(symbolFill).length > 0)
      );
    }

    /** Règle qui définit une taille de symbole ponctuel. */
    ruleDefinesSize(rule: StyleRule): boolean {
      const size = rule && rule.style && rule.style.pointSymbol
        ? rule.style.pointSymbol.size
        : undefined;
      return size !== undefined && size !== null && size !== '';
    }

    /** Série couleur pure (ex: categorie) — pas de taille propre. */
    isColorOnlyRule(rule: StyleRule): boolean {
      return this.ruleDefinesColor(rule) && !this.ruleDefinesSize(rule);
    }

    /** Série taille pure (ex: population) — pas de fill propre. */
    isSizeOnlyRule(rule: StyleRule): boolean {
      return this.ruleDefinesSize(rule) && !this.ruleDefinesColor(rule);
    }

    /**
     * Règles utiles pour les indicateurs (couleurs).
     * En analyse bivariée (couleur + taille), on ne garde que les règles couleur.
     */
    getColorRules(): StyleRule[] {
      const colorOnly = this.styleRules.filter((rule) => this.isColorOnlyRule(rule));
      if (colorOnly.length > 0) {
        return colorOnly;
      }
      return this.styleRules.filter((rule) => this.ruleDefinesColor(rule));
    }

    /** Règles de taille pure (série quantitative). */
    getSizeRules(): StyleRule[] {
      return this.styleRules.filter((rule) => this.isSizeOnlyRule(rule));
    }

    /** Analyse bivariée : au moins une série couleur et une série taille. */
    isBivariate(): boolean {
      return this.getColorRules().some((rule) => this.isColorOnlyRule(rule))
        && this.getSizeRules().length > 0;
    }

    /**
     * Fusionne une règle couleur + une règle taille (bivarié).
     * N'utilise pas setValues (trop destructif : écrase couleurs / icônes).
     */
    mergeColorAndSizeStyles(colorRule: StyleRule, sizeRule: StyleRule): LayerStyle {
      const colorStyle = colorRule.style;
      const size = sizeRule.style && sizeRule.style.pointSymbol
        ? sizeRule.style.pointSymbol.size
        : undefined;
      const colorIdx = this.styleRules.indexOf(colorRule);
      const cacheKey = `${colorIdx}|${size}`;
      const cached = this.mergedStyleCache[cacheKey];
      if (cached) {
        // Si le SVG de la règle source a fini de charger après le 1er merge, le propager
        const hadSvg = !!(cached.pointSymbol && cached.pointSymbol.svgData);
        this.copySvgRuntimeState(colorStyle, cached);
        if (!hadSvg && cached.pointSymbol && cached.pointSymbol.svgData) {
          cached.clearCache();
        }
        return cached;
      }

      const merged = colorStyle.clone();
      const baseStyle = (colorStyle as any).baseStyle;
      if (baseStyle) {
        merged.setBaseStyle(baseStyle);
      }
      if (size !== undefined && size !== null && size !== '') {
        merged.pointSymbol.size = size;
      }
      // clone()/toJson() ne sérialise pas svgData ni imgcache : sans copie,
      // chaque évaluation relance un fetch async et le picto SVG n'apparaît jamais.
      this.copySvgRuntimeState(colorStyle, merged);
      merged.clearCache();
      this.mergedStyleCache[cacheKey] = merged;
      return merged;
    }

    /** Recopie l'état runtime SVG (non présent dans toJson) d'un style source vers une cible. */
    private copySvgRuntimeState(source: LayerStyle, target: LayerStyle): void {
      if (!source || !source.pointSymbol || !target || !target.pointSymbol) {
        return;
      }
      if (source.pointSymbol.svgData && !target.pointSymbol.svgData) {
        target.pointSymbol.svgData = source.pointSymbol.svgData;
      }
      if (source.pointSymbol.imgcache && !target.pointSymbol.imgcache) {
        target.pointSymbol.imgcache = source.pointSymbol.imgcache;
      }
    }

    getColorFromAnalyse(val: number, attributeName: string): any {
      let f=new Feature();
      f.set(attributeName, val);
      const colorRules = this.getColorRules().filter((v:StyleRule) => {
        return v.active && v.evaluateFeature(f);
      });
      let targetCategory=colorRules[0];
      if (targetCategory) {
        return targetCategory.style.fillStyle.fillColor
          || targetCategory.style.pointSymbol.symbolFillColor
          || targetCategory.style.strokeStyle.color;
      }
    }

    getStyleForFeature(feature: any, resolution?: number): LayerStyle|null {
      let scaleDenominator:any;
      if (resolution) {
        scaleDenominator = MapUtils.getScaleFromResolution(resolution, 'm', true);
      }

      // Uniquement les règles actives matchantes et dans l'échelle
      // (plus de nullification globale si une règle inactive matche aussi)
      const matchedRules = this.styleRules.filter((v:StyleRule) => {
        if (!v.active) {
          return false;
        }
        if (!v.inRange(scaleDenominator)) {
          return false;
        }
        return v.evaluateFeature(feature);
      });

      if (matchedRules.length === 0) {
        return null;
      }

      // Bivarié : une règle couleur (+ éventuellement taille sur une autre série)
      const colorOnly = matchedRules.filter((rule) => this.isColorOnlyRule(rule));
      const sizeRules = matchedRules.filter((rule) => this.ruleDefinesSize(rule));
      if (colorOnly.length > 0 && sizeRules.length > 0) {
        return this.mergeColorAndSizeStyles(colorOnly[0], sizeRules[0]);
      }

      // Filtre donut (règles couleur partiellement désactivées) :
      // ne pas afficher les objets qui ne matchent qu'une règle de taille.
      const layerHasColorOnly = this.styleRules.some((rule) => this.isColorOnlyRule(rule));
      const someColorInactive = this.styleRules.some(
        (rule) => this.isColorOnlyRule(rule) && !rule.active
      );
      if (layerHasColorOnly && someColorInactive && colorOnly.length === 0 && sizeRules.length > 0) {
        return null;
      }

      // Comportement historique : première règle matchante
      return matchedRules[0].style;
    }

    getActiveCQLFilter():string {
      const activeList = this.styleRules.filter((x) => x.active);
      if (activeList && activeList.length === this.styleRules.length) {
        // all active
        return '';
      }
      let retour = '';
      activeList.forEach((rule:StyleRule) => {
        if (retour && retour.length > 0) {
          retour = `(${retour} AND ${rule.value})`;
        } else {
          retour = rule.value;
        }
      });
      return retour;
    }

    getStyle(feature: any, resolution?: number): Style[]|null {
      const featureStyle = this.getStyleForFeature(feature, resolution);

      if (featureStyle) {
        return featureStyle.getStyle(feature);
      }

      return null;
    }
}
